import { ParsedUrlQuery } from 'querystring'

import { LocaleType } from '@hhgtech/hhg-components/build/interfaces/types'
import slugify from 'slugify'
import _formatString from 'string-format'

import { domainLocales } from 'domainLocales'
import {
  basePath,
  CARE_LOCALISED_SLUGS,
  CareEmbeddedTypeToSlugType,
  DOMAIN_MARRY_BABY,
  ENV,
  SSO_URL,
} from 'interfaces/constants'
import type { CareEmbeddedType, RedirectSourceType } from 'interfaces/types'
import { LOCALE } from 'interfaces/types/Locale'

// import { PATHS } from './paths'

// wrapper to remove empty params from url
// exp: abc?a=1&b=&c=2 => abc?a=1&c=2
const locale = (ENV.NEXT_PUBLIC_DEPLOY_LOCALE || 'en-PH') as LOCALE
const formatString = (...params: Parameters<typeof _formatString>) => {
  const formatString = _formatString(...params)
  if (formatString.includes('?')) {
    const [url, query] = formatString.split('?', 2)
    const queryParams = query
      .split('&')
      .filter((param) => param && (!param.includes('=') || param.split('=')[1]))
    if (queryParams.length === 0) {
      return url
    }
    return `${url}?${queryParams.join('&')}`
  }
  return formatString
}

export const getLocalApiPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  const { NEXT_PUBLIC_BASE_API_URL } = ENV
  const apiUrl = NEXT_PUBLIC_BASE_API_URL
  return apiUrl + 'wp-json/api/' + formatString(path, { ...params })
}

export const getApiPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  const { NEXT_PUBLIC_BASE_API_URL, WP_INTERNAL_API } = ENV
  const apiUrl = WP_INTERNAL_API || NEXT_PUBLIC_BASE_API_URL
  return apiUrl + 'wp-json/api/' + formatString(path, { ...params })
}

export const getApiTrackingPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  return (
    ENV.NEXT_PUBLIC_BASE_API_URL +
    'wp-json/' +
    formatString(path, { ...params })
  )
}

export const getSidisSubscriptionPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  return (
    ENV.NEXT_PUBLIC_SIDIS_API_GET +
    'api/' +
    formatString(path, { ...(params || {}) })
  )
}

export const getLeadPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  return ENV.NEXT_PUBLIC_LEAD_API + 'api/' + formatString(path, { ...params })
}

export const getTogetherPath = (
  path: string,
  params?: { [key: string]: string | number },
  version?: string,
) => {
  return (
    (typeof window !== 'undefined'
      ? ENV.NEXT_PUBLIC_TOGETHER_API
      : ENV.INTERNAL_TOGETHER_API || ENV.NEXT_PUBLIC_TOGETHER_API) +
    `api/${version || 'v1'}/` +
    formatString(path, { ...params })
  )
}

export const getCarePath = (
  path: string,
  params?: { [key: string]: string | number | undefined },
) => {
  return (
    (typeof window !== 'undefined'
      ? ENV.NEXT_PUBLIC_CARE_API
      : ENV.INTERNAL_CARE_API || ENV.NEXT_PUBLIC_CARE_API) +
    'api/' +
    formatString(path, { ...params, site: domainLocales[locale as LOCALE] })
  )
}

export const getCurrentBaseUrl = (locale: LOCALE) => {
  if (typeof window !== 'undefined') return window.location.origin
  const isStaging = ENV.DEPLOY_ENV === 'staging'
  const isDev = ENV.DEPLOY_ENV === 'development'
  return isDev
    ? 'http://localhost:3000'
    : `https://${isStaging ? 'discover.' : ''}${domainLocales[locale]}`
}

export const getMarryBabyDomain = () => {
  const isProduction = ENV.DEPLOY_ENV === 'production'
  if (!isProduction) {
    return 'https://fe.marrybaby.vn'
  }
  return DOMAIN_MARRY_BABY
}

export const getCareDomain = (locale: LOCALE) => {
  const isProduction = ENV.DEPLOY_ENV === 'production'

  if (isProduction) {
    return `${domainLocales[locale]}/care`
  }

  return `discover.${domainLocales[locale]}/care`
}

export const slugifiedNameWithId = (
  name?: string,
  id?: number,
  _locale?: LocaleType,
  slug?: string,
) => {
  if (slug && id) {
    return `${slug}-${id}`
  }

  return `${slugify(name || '', {
    lower: true,
    locale: _locale || locale,
  })}-${id}`
}

export const getHospitalUrl = (locale: LOCALE) => {
  return `${CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.HOSPITAL}`
}

export const getServiceUrl = ({
  locale,
  citySlug,
}: {
  citySlug?: string
  locale: LocaleType
}) => {
  return `/care/${slugify(
    citySlug || CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.CURRENT || '',
    {
      lower: true,
      locale,
    },
  )}/${CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.SERVICE}`
}

export const getCareAllSpecialtyUrl = ({ locale }: { locale: LocaleType }) => {
  return `/care/${CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.SPECIALTIES}`
}

export const getCareSpecialtyUrl = ({
  locale,
  citySlug,
  slug,
}: {
  citySlug?: string
  slug?: string
  locale: LocaleType
}) => {
  return `/care/${slugify(
    citySlug || CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.ALL || '',
    {
      lower: true,
      locale,
    },
  )}/${CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.HOSPITAL}/${slug}`
}

export const getCareHospitalUrl = ({
  name,
  id,
  locale,
  slug,
}: {
  name?: string
  id?: number
  slug?: string
  locale: LocaleType
}) => {
  return `/care/${
    CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.HOSPITAL
  }/${slugifiedNameWithId(name, id, locale, slug)}`
}

export const getCareDoctorUrl = ({
  name,
  id,
  locale,
}: {
  name?: string
  id?: number
  locale: LocaleType
}) => {
  return `/care/${
    CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.DOCTOR
  }/${slugifiedNameWithId(name, id)}`
}

export const getCareServiceUrl = ({
  name,
  id,
  locale,
}: {
  name: string
  id: number
  locale: LocaleType
}) => {
  return `/care/${
    CARE_LOCALISED_SLUGS[locale || 'vi-VN']?.SERVICE
  }/${slugifiedNameWithId(name, id)}`
}

export const getCareVaccinationUrl = (locale: LOCALE) => {
  const localePath = CARE_LOCALISED_SLUGS[locale || 'vi-VN']
  return `/care/${localePath?.SERVICE}/${localePath?.VACCINATIONS}`
}

export const getCareSearchPageUrl = ({
  type,
  locale,
  searchSlug,
}: {
  type: CareEmbeddedType
  searchSlug: string
  locale: LocaleType
}) => {
  return `/care/${CARE_LOCALISED_SLUGS[locale]?.CURRENT}/${
    CARE_LOCALISED_SLUGS[locale]?.[CareEmbeddedTypeToSlugType[type]]
  }/${searchSlug ?? ''}`
}

export const getBabyVaccineToolPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  return (
    (ENV.INTERNAL_VACCINATION_TOOL_API ||
      ENV.NEXT_PUBLIC_VACCINATION_TOOL_API) +
    formatString(path, {
      ...params,
      domain: domainLocales[locale],
    })
  )
}

export const cleanUnicodeUrl = (s: string) =>
  s
    .split('/')
    .map((s) => (s.includes('%') ? s : encodeURI(s)))
    .join('/')

export const getUrlToRedirectToSSOWithReturnUrl = (
  url?: string,
  source?: RedirectSourceType,
  action?: string,
): string => {
  if (typeof window === 'undefined') {
    return ''
  }

  const originalRedirectUrl = url || window.location.href

  const sourceRedirect = !!source
    ? `${originalRedirectUrl.includes('?') ? '&' : '?'}source=${source}`
    : ''

  const actionRedirect = !!action
    ? `${
        originalRedirectUrl.includes('?') || !!sourceRedirect ? '&' : '?'
      }action=${action}`
    : ''
  return SSO_URL
    ? `${SSO_URL}?returnUrl=${encodeURIComponent(
        `${originalRedirectUrl}${sourceRedirect}${actionRedirect}`,
      )}`
    : '/'
}

export const getSubotApiPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  const { SUBOT_INTERNAL, NEXT_PUBLIC_SUBOT_API } = ENV
  const apiUrl =
    typeof window === 'undefined'
      ? SUBOT_INTERNAL || NEXT_PUBLIC_SUBOT_API
      : NEXT_PUBLIC_SUBOT_API
  return apiUrl + formatString(path, { ...params })
}

export const getEcommerceApiPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  const { NEXT_PUBLIC_ECOMMERCE_API } = ENV
  return (
    (NEXT_PUBLIC_ECOMMERCE_API || 'https://uat-ecommerce.hellobacsi.com/') +
    'rest/' +
    ENV.NEXT_PUBLIC_STORE_CODE +
    '/V1/' +
    formatString(path, { ...params })
  )
}

export const getChatbotApiPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  const {
    NEXT_PUBLIC_CHAT_BOT_API = 'https://chatbot-staging.hellohealthgroup.com/',
  } = ENV
  return NEXT_PUBLIC_CHAT_BOT_API + formatString(path, { ...params })
}

export const getHelloSitesUrl = (
  locale: LOCALE,
  ignoreLocal?: boolean,
  ignoreDeployEnv = false,
) => {
  const { DEPLOY_ENV } = ENV

  const isProd = DEPLOY_ENV === 'production'
  const isLocalhost = ignoreLocal
    ? false
    : typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1')

  if (ignoreDeployEnv) {
    return `https://${domainLocales[locale]}`
  }
  if (isLocalhost) {
    return `http://${window.location.host}` // or any other localhost URL you want to use as fallback
  }
  return `https://${isProd ? '' : 'discover.'}${domainLocales[locale]}`
}

export function getHellositePath({
  locale,
  slug: slugProps,
  query: queryProps,
}: {
  locale: LOCALE
  slug?: string | string[]
  query?: ParsedUrlQuery
}): string {
  let basePath = getHelloSitesUrl(locale)

  // For PhilippinesTagalog
  if (locale === 'tl-PH') {
    basePath += '/fil'
  }

  let query = '?'
  if (typeof queryProps !== 'undefined') {
    let sep = ''
    Object.keys(queryProps).map((key) => {
      const queryValue = queryProps[key]
      if (Array.isArray(queryValue)) {
        query += sep + queryValue.map((val) => `${key}=${val}`).join('&')
        sep = '&'
      }
      if (typeof queryValue === 'string') {
        query += sep + `${key}=${queryValue}`
        sep = '&'
      }
    })
  }
  // Remove `?` if not have any queries
  if (query === '?') {
    query = ''
  }

  let slug: string[] = []
  if (typeof slugProps === 'string') {
    slug = slugProps.split('/').filter(Boolean)
  }

  if (Array.isArray(slugProps)) {
    slug = slugProps.map((s) => {
      return s.split('/').filter(Boolean).join('/')
    }, [])
  }

  return basePath + '/' + slug.join('/') + '/' + query
}

export const removeBasePath = (s: string) =>
  basePath ? s.replace(basePath, '') : s

export const getSidisGetPath = (
  path: string,
  params?: { [key: string]: string | number },
) => {
  return (
    ENV.NEXT_PUBLIC_SIDIS_API_GET +
    'frontend/' +
    formatString(path, { ...params })
  )
}

// export const getSidisTemplatePath = (
//   path: string,
//   params?: { [key: string]: string | number },
// ) => {
//   return ENV.NEXT_PUBLIC_SIDIS_TEMPLATE_API + formatString(path, { ...params })
// }

// export const getSidisSubscriptionPath = (
//   path: string,
//   params?: { [key: string]: string | number },
// ) => {
//   return (
//     ENV.NEXT_PUBLIC_SIDIS_API_GET +
//     'api/' +
//     formatString(path, { ...(params || {}) })
//   )
// }

// export const getSidisUpdatePath = (
//   path: string,
//   params?: { [key: string]: string | number },
// ) => {
//   return ENV.NEXT_PUBLIC_SIDIS_API + 'api/' + formatString(path, { ...params })
// }

export function getPathURLSearchParams(
  path: string,
  searchParams?: URLSearchParams,
): string {
  let newUrl = path
  const newSearchParams = new URLSearchParams(searchParams)
  let count = 0
  newSearchParams.forEach((value, key) => {
    if (!value) {
      newSearchParams.delete(key)
    } else {
      count++
    }
  })
  if (count !== 0) {
    newUrl += `?${newSearchParams}`
  }
  return newUrl
}
